import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import BankServices from "../../../Services/QRCodeServices/BankServices/BankServices";

const BankAddEditModal = ({
  isOpen,
  toggle,
  isEdit,
  selectedBank,
  onSuccess,
}) => {
  // Validation schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    bankCode: Yup.string().required("Bank Code is required"),
    accountNumber: Yup.string().required("Account Number is required"),
    openingBalance: isEdit
      ? Yup.number()
          .min(0, "Opening Balance cannot be negative")
          .required("Opening Balance is required")
      : Yup.number().min(0, "Opening Balance cannot be negative"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      name: selectedBank?.name || "",
      bankCode: selectedBank?.bankCode || "",
      accountNumber: selectedBank?.accountNumber || "",
      openingBalance: selectedBank?.openingBalance || 0,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,

    onSubmit: async (values) => {
      try {
        // Create the base payload
        const payload = {
          id: isEdit ? selectedBank.id : undefined,
          name: values.name,
          bankCode: values.bankCode,
          accountNumber: values.accountNumber,
        };

        // Add opening balance only if not in edit mode
        if (!isEdit) {
          payload.openingBalance = Number(values.openingBalance);
        }

        if (isEdit) {
          await BankServices.update(selectedBank.id, payload);
          toast.success("Bank updated successfully!");
        } else {
          await BankServices.create(payload);
          toast.success("Bank created successfully!");
        }
        onSuccess();
        formik.resetForm();
      } catch (error) {
        toast.error(error.response?.data || "Failed to save bank");
        console.error("Error saving bank:", error);
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={handleCancel}>
        {isEdit ? "Edit Bank" : "Create Bank"}
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={12} className="mb-3">
              <Label for="name">Name</Label>
              <Input
                type="text"
                id="name"
                name="name"
                placeholder="Enter Bank Name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                invalid={formik.touched.name && formik.errors.name}
              />
              {formik.touched.name && formik.errors.name && (
                <FormFeedback>{formik.errors.name}</FormFeedback>
              )}
            </Col>
          </Row>
          {!isEdit && (
            <Row>
              <Col md={12} className="mb-3">
                <Label for="bankCode">Bank Code</Label>
                <Input
                  type="text"
                  id="bankCode"
                  name="bankCode"
                  placeholder="Enter Bank Code"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.bankCode}
                  invalid={formik.touched.bankCode && formik.errors.bankCode}
                />
                {formik.touched.bankCode && formik.errors.bankCode && (
                  <FormFeedback>{formik.errors.bankCode}</FormFeedback>
                )}
              </Col>
            </Row>
          )}
          <Row>
            <Col md={12} className="mb-3">
              <Label for="accountNumber">Account Number</Label>
              <Input
                type="text"
                id="accountNumber"
                name="accountNumber"
                placeholder="Enter Account Number"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.accountNumber}
                invalid={
                  formik.touched.accountNumber && formik.errors.accountNumber
                }
              />
              {formik.touched.accountNumber && formik.errors.accountNumber && (
                <FormFeedback>{formik.errors.accountNumber}</FormFeedback>
              )}
            </Col>
          </Row>
          {!isEdit && (
            <Row>
              <Col md={12} className="mb-3">
                <Label for="openingBalance">Opening Balance</Label>
                <Input
                  type="number"
                  id="openingBalance"
                  name="openingBalance"
                  placeholder="Enter Opening Balance"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.openingBalance}
                  invalid={
                    formik.touched.openingBalance &&
                    formik.errors.openingBalance
                  }
                />
                {formik.touched.openingBalance &&
                  formik.errors.openingBalance && (
                    <FormFeedback>{formik.errors.openingBalance}</FormFeedback>
                  )}
              </Col>
            </Row>
          )}
         
        </ModalBody>
        <ModalFooter>
          <Button type="submit" color="success" disabled={formik.isSubmitting}>
            {isEdit ? "Update" : "Save"}
          </Button>
          <Button color="danger" onClick={handleCancel} type="button">
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default BankAddEditModal;
